import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Guid from "common/values/guid/guid";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import { ProposalField } from "work/entities/proposal/proposal";
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import CommentsButton from "work/entities/comment-thread/view/comments-button";

const RowTitle = styled(Typography)(
  () => ({
    fontWeight: "500",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }));
const RowActionsContainer = styled("div")(
  () => ({
    whiteSpace: 'nowrap'
  }));

type FeeScheduleCategoriesReadonlyProps = {
  className?: string;
  categories: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  commentThreads?: CommentThread[];
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  hideCommentButton?: boolean;
};

export default function FeeScheduleCategoriesReadonly(
  props: Readonly<FeeScheduleCategoriesReadonlyProps>
) {
  const {
    className,
    categories,
    commentThreads,
    onCommentsClicked,
    hideCommentButton
  } = props;

  const session = useSession();

  const columnHelper = createMRTColumnHelper<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>();
  const columns = [
    columnHelper.accessor(
      (category) => <RowTitle>{category.name.value}</RowTitle>,
      {
        header: "Name",
        id: "name",
      }
    ),
    columnHelper.accessor(
      (category) => category.description?.value,
      {
        header: "Description",
        id: "description",
      }
    ),
    columnHelper.accessor(
      (category) => {
        let fixedValue;
        if (category instanceof FeeScheduleCategoryMaskedCurrency) {
          fixedValue = "Masked";
        } else if (!category?.fee?.unit) {
          fixedValue = "Yes";
        } else {
          fixedValue = "No";
        }
        return fixedValue;
      },
      {
        header: "Fixed?",
        id: "fixedFee",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (category) => {
        const deferredTo = session.context?.viewingAsVendor === true ? "client" : "vendor";
        let feeValue;
        if (category instanceof FeeScheduleCategoryMaskedCurrency) {
          feeValue = "Masked";
        } else if (!category.fee) {
          feeValue = <Typography color="warning">{`Deferred to ${deferredTo}`}</Typography>;
        } else {
          feeValue = new Intl.NumberFormat(
            navigator.language,
            {
              style: "currency",
              currency: category.fee?.rate?.currency.toString() ?? "USD",
            }
          ).format(category.fee?.rate?.amount ?? 0);
        }
        return feeValue;
      },
      {
        header: "Fee",
        id: "fee",
        size: 50
      }
    ),
    columnHelper.accessor(
      (category) =>
        category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" :
          category.fee?.unit ?? <Icon>remove</Icon>,
      {
        header: "Per",
        id: "unit",
        size: 50
      }
    ),
    columnHelper.accessor(
      (category) =>
        category.billingCode?.value && category.billingCode.value.length > 0 ? (
          category.billingCode.value
        ) : (
          <Icon>remove</Icon>
        ),
      {
        header: "Code",
        id: "billingCode",
        size: 50
      }
    ),
  ];
  const table = useMaterialReactTable({
    columns,
    data: categories,
    enableRowSelection: false,
    enableTableHead: true,
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: { right: ['mrt-row-actions'] }
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    enableRowActions: !hideCommentButton,
    muiTableBodyRowProps: () => ({
      sx: {
        pointerEvents: "none"
      }
    }),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <ToggleGlobalFilterButton table={table} />
        <ToggleFiltersButton table={table} />
        <ShowHideColumnsButton table={table} />
        <ToggleDensePaddingButton table={table} />
        <ToggleFullScreenButton table={table} />
        {!hideCommentButton && (
          <CommentsButton
            commentThreads={commentThreads}
            hideUnreadCount={true}
            field={ProposalField.FeeSchedule}
            toolTip={
              commentThreads?.some((thread) =>
                thread.field.isEqualTo(ProposalField.FeeSchedule)) ?
                "Comments" : "No Comments"
            }
            onCommentsClicked={
              onCommentsClicked ? () => {
                onCommentsClicked(ProposalField.FeeSchedule);
              } : undefined
            }
          />
        )}
      </Box>
    ),
    renderRowActions: ({ row }) => {
      return (
        <RowActionsContainer>
          <CommentsButton
            field={ProposalField.FeeScheduleCategory(row.original.id)}
            hideUnreadCount={true}
            commentThreads={commentThreads}
            toolTip={commentThreads?.some((thread) =>
              thread.field.isEqualTo(ProposalField.FeeScheduleCategory(row.original.id)))
              ? "Comments" : "No Comments"
            }
            onCommentsClicked={
              onCommentsClicked ? () => {
                onCommentsClicked(ProposalField.FeeScheduleCategory(row.original.id));
              } : undefined
            }
          />
        </RowActionsContainer>
      );
    },
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight ??
          0}px - ${table.refs.bottomToolbarRef.current?.offsetHeight ?? 0}px)`,
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: "100%",
      },
    },
  });

  return (
    <div className={className}>
      <MaterialReactTable table={table} />
    </div>
  );
}
